import { useState, useEffect, useRef } from 'react'
import type { ReactNode } from 'react'
import { Check } from '@digital-twins/design-system/icons'
import { cn } from '@/lib/utils'

function CardCoverImage({ src }: { src: string }) {
  const [loaded, setLoaded] = useState(false)
  const imgRef = useRef<HTMLImageElement>(null)

  useEffect(() => {
    setLoaded(false)
    const img = imgRef.current
    if (img?.complete && img.getAttribute('src') === src) {
      setLoaded(true)
    }
  }, [src])

  return (
    <img
      ref={imgRef}
      key={src}
      src={src}
      alt=""
      loading="eager"
      decoding="async"
      className={cn(
        'absolute inset-0 h-full w-full object-cover object-center',
        'transition-[transform,opacity] duration-500 ease-out group-hover:scale-105',
        loaded ? 'opacity-100' : 'opacity-0',
      )}
      onLoad={() => setLoaded(true)}
    />
  )
}

interface HomeCarouselCardFrameProps {
  imageUrl?: string | null
  isActive: boolean
  ariaLabel: string
  onClick: () => void
  topLeft?: ReactNode
  bottomContent: ReactNode
  sizeClassName?: string
  className?: string
}

export function HomeCarouselCardFrame({
  imageUrl,
  isActive,
  ariaLabel,
  onClick,
  topLeft,
  bottomContent,
  sizeClassName = 'h-[300px] w-[276px]',
  className,
}: HomeCarouselCardFrameProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={ariaLabel}
      className={cn(
        'group relative overflow-hidden rounded-lg text-left transition-[transform,box-shadow] duration-300 ease-out hover:-translate-y-1',
        sizeClassName,
        className,
      )}
      style={{
        boxShadow: isActive
          ? 'var(--cinematic-card-shadow, var(--home-card-shadow-active))'
          : 'var(--cinematic-card-shadow, var(--home-card-shadow))',
      }}
    >
      {/* Themed fallback — visible while image loads or when no cover is set */}
      <div
        className="absolute inset-0"
        style={{ background: 'var(--home-card-placeholder-gradient)' }}
      />
      {imageUrl && <CardCoverImage src={imageUrl} />}

      <div
        className="absolute inset-0 transition-all duration-300 group-hover:opacity-100"
        style={{ background: 'linear-gradient(0deg, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.1) 40%, rgba(0,0,0,0) 60%)' }}
      />

      {isActive ? (
        <div
          className="pointer-events-none absolute inset-0 rounded-lg border-2"
          style={{ borderColor: 'var(--home-card-active-border)' }}
        />
      ) : null}

      <div className="absolute inset-x-0 top-0 flex items-start justify-between p-3">
        <div className="min-w-0">{topLeft}</div>

        <div
          className={cn(
            'flex h-[26px] w-[26px] items-center justify-center rounded-full border backdrop-blur-sm',
            isActive
              ? 'border-transparent'
              : 'border-white/55 bg-white/10 text-white',
          )}
          style={isActive
            ? {
                backgroundColor: 'var(--home-card-active-check-bg)',
                color: 'var(--home-card-active-check-fg)',
              }
            : undefined}
        >
          {isActive ? (
            <Check className="h-3.5 w-3.5" strokeWidth={2.2} />
          ) : (
            <span className="h-3 w-3 rounded-full border border-white/70 opacity-0" />
          )}
        </div>
      </div>

      <div className="absolute inset-x-0 bottom-0 p-3">
        {bottomContent}
      </div>
    </button>
  )
}
